import { ApiError, errorCode } from '../api/client'
import {
  fetchSqlColumns,
  fetchSqlTables,
  fetchSqlValues,
  runSqlQuery,
  type SqlColumn,
  type SqlFilter,
} from '../api/sql'
import { dataTableKey, hideDataTable, showDataTable, type DataTableRow } from './dataTable'
import { addLogEntry } from './debugLog'

/**
 * Guided SQL "Data Requests" modal (PRD Phase 2, M11). The viewer renders
 * exactly what the proxy advertises — tables come pre-filtered to the plan's
 * layer access, columns/values are the server-approved sets, and the query is
 * validated server-side. "Show on map" zooms to the row's bbox_3857 (no WFS).
 * Plan/feature 403s render inline; session-level errors escalate to the app
 * shell's enforcement handler.
 */
export interface SqlPanelOptions {
  /** The right panel's pane to fill (T5) — the modal shell is gone. */
  mount: HTMLElement
  onShowBbox: (bbox: [number, number, number, number]) => void
  onEnforcement: (err: ApiError) => void
}

export interface SqlPanel {
  /** Lazily fetch the dataset list on first reveal. Idempotent. */
  ensureLoaded(): void
  dispose(): void
}

const PAGE_SIZE = 25
/** Owner key for the shared bottom table — distinct from any layer name. */
const SQL_TABLE_KEY = 'sql'
const MAX_FILTERS = 8
const TYPEAHEAD_DEBOUNCE_MS = 300

// v1.1: string sample columns get a server-backed type-ahead (`values?q=`)
// and submit op "contains"; distinct dropdowns and date/number samples stay "=".
const isContainsColumn = (c: SqlColumn): boolean => c.type === 'string' && c.preview === 'sample'

// v1.2: number columns take comparisons. The rule is derived from the column
// type rather than announced by the server — the v1.1 pattern, duplicated on
// both sides by design (contract §3). Its price is a deployment order: a proxy
// older than v1.2 answers 422 to these ops.
const isNumericColumn = (c: SqlColumn): boolean => c.type === 'number'

/** Displayed operator → what the contract calls it. `=` needs no entry. */
const COMPARISON_OPS: Array<{ value: SqlFilter['op']; label: string; title: string }> = [
  { value: '=', label: '=', title: 'exactly' },
  { value: '>=', label: '≥', title: 'at least' },
  { value: '<=', label: '≤', title: 'at most' },
]

export function createSqlPanel(options: SqlPanelOptions): SqlPanel {
  const root = options.mount
  root.classList.add('sql-form')
  root.innerHTML = `
    <div class="sql-form__asof" id="sql-asof"></div>
    <label class="sql-form__dataset">Dataset
      <select id="sql-table" aria-label="Dataset"></select>
    </label>
    <div id="sql-filters"></div>
    <div class="sql-form__actions">
      <button type="button" class="btn btn--small" id="sql-add-filter" disabled>Add filter</button>
      <button type="button" class="btn btn--small btn--primary" id="sql-run">Run</button>
    </div>
    <p class="sql-form__error" id="sql-error" hidden></p>
    <div class="sql-form__status" id="sql-results"></div>`

  const el = <T extends HTMLElement>(id: string): T => root.querySelector(`#${id}`) as T
  const tableSelect = el<HTMLSelectElement>('sql-table')
  const filtersEl = el<HTMLDivElement>('sql-filters')
  const errorEl = el<HTMLParagraphElement>('sql-error')
  const statusEl = el<HTMLDivElement>('sql-results')
  const addFilterBtn = el<HTMLButtonElement>('sql-add-filter')

  let loaded = false
  let columns: SqlColumn[] = []
  let columnsSeq = 0
  // Which filter row's /values fetch (if any) wrote the currently displayed
  // error — null for a dataset-level or Run failure. A row's own success may
  // only clear an error it wrote itself, never one from another row or from
  // Run (§3).
  let errorOwner: HTMLDivElement | null = null

  function showError(message: string, owner: HTMLDivElement | null = null): void {
    errorEl.textContent = message
    errorEl.hidden = false
    errorOwner = owner
  }

  function handleFailure(err: unknown, owner: HTMLDivElement | null = null): void {
    if (!(err instanceof ApiError)) {
      showError('Request failed. Please retry.', owner)
      return
    }
    // Feature-scoped denials stay in the form; session-level problems go to the
    // app shell, which replaces the whole screen — nothing to close by hand.
    if (err.kind === 'service' || err.kind === 'plan' || err.kind === 'layer') {
      showError('This dataset is not included in your plan.', owner)
    } else if (err.kind === 'unavailable') {
      // The data copy warming up (or /query failing the same way) is a
      // transient, feature-scoped condition — it stays in the form, not a
      // full-screen state or a toast (unlike a 503 on a map tile). It still
      // belongs in the diagnostics log ("Copy problem details") and carries
      // its DOWN-503-style code, same as an enforcement-routed error.
      addLogEntry(err.kind, err.status, err.message)
      const message = err.message || 'Data service is warming up — please retry in a minute.'
      showError(`${message} (code ${errorCode(err.kind, err.status)})`, owner)
    } else if (err.kind === 'unknown') {
      showError(err.message, owner)
    } else {
      options.onEnforcement(err)
    }
  }

  async function loadTables(): Promise<void> {
    const { tables, data_as_of } = await fetchSqlTables()
    tableSelect.innerHTML = ''
    for (const table of tables) {
      const option = document.createElement('option')
      option.value = table.id
      option.textContent = table.label
      tableSelect.appendChild(option)
    }
    el<HTMLSpanElement>('sql-asof').textContent = data_as_of
      ? `data as of ${new Date(data_as_of).toLocaleString()}`
      : ''
    if (tables.length === 0) showError('No datasets are included in your plan.')
    else await loadColumns()
  }

  async function loadColumns(): Promise<void> {
    // "Add filter" stays disabled until the selected table's columns are in;
    // the sequence guard drops stale responses when tables switch quickly.
    const seq = ++columnsSeq
    addFilterBtn.disabled = true
    filtersEl.innerHTML = ''
    statusEl.innerHTML = ''
    // Results belong to the dataset that produced them — switching datasets
    // must not leave the previous rows sitting in the table.
    if (dataTableKey() === SQL_TABLE_KEY) hideDataTable()
    errorEl.hidden = true
    errorOwner = null
    const loadedColumns = await fetchSqlColumns(tableSelect.value)
    if (seq !== columnsSeq) return
    columns = loadedColumns
    addFilterBtn.disabled = false
    // Nobody opens the Data Filter to look at an empty form: the first filter
    // row is offered outright, so "Add filter" is only for the second one on.
    if (columns.length > 0) addFilterRow()
  }

  async function buildValueControl(row: HTMLDivElement, column: SqlColumn): Promise<void> {
    // Sequence guard: switching columns fires overlapping value fetches —
    // only the latest selection may render its control.
    const seq = String(Number(row.dataset.seq ?? '0') + 1)
    row.dataset.seq = seq
    const wrap = row.querySelector('.sql-filter__valwrap') as HTMLElement
    wrap.innerHTML = ''
    // The value slot must be usable the instant a column is picked — the
    // suggestions call (distinct list / sample + type-ahead) is a progressive
    // enhancement layered on top, not a precondition. This also means a 503
    // from /values (proxy "warming up") leaves a working "=" input behind
    // instead of an empty slot.
    const input = document.createElement('input')
    input.type = 'text'
    input.className = 'sql-filter__val'
    input.placeholder = 'value'
    wrap.appendChild(input)
    try {
      const { kind, values } = await fetchSqlValues(tableSelect.value, column.name)
      // A late response must not touch a row that changed column (seq) or was
      // discarded outright — a dataset switch clears `#sql-filters` wholesale
      // and the × button removes a row without bumping seq, so `isConnected`
      // is the guard those two paths still need.
      if (row.dataset.seq !== seq || !row.isConnected) return
      // Only clear the error if this row's own /values fetch is the one that
      // wrote it — a sibling row's failure, or Run's, is not this row's to
      // dismiss (§3).
      if (errorOwner === row) {
        errorEl.hidden = true
        errorOwner = null
      }
      if (kind === 'distinct') {
        // The user may already have typed a value while this was in flight —
        // "=" on any value is accepted server-side, so an already-filled input
        // wins over the dropdown rather than being clobbered.
        if (input.value !== '') return
        const select = document.createElement('select')
        select.className = 'sql-filter__val'
        for (const value of values) {
          const option = document.createElement('option')
          option.value = value
          option.textContent = value
          select.appendChild(option)
        }
        wrap.innerHTML = ''
        wrap.appendChild(select)
      } else {
        input.placeholder = values.length ? `e.g. ${values[0]}` : 'value'
        const listId = `sql-dl-${column.name}-${Date.now() % 100000}`
        const datalist = document.createElement('datalist')
        datalist.id = listId
        const fillDatalist = (entries: string[]): void => {
          datalist.innerHTML = ''
          for (const value of entries) {
            const option = document.createElement('option')
            option.value = value
            datalist.appendChild(option)
          }
        }
        fillDatalist(values)
        input.setAttribute('list', listId)
        wrap.appendChild(datalist)
        if (isContainsColumn(column)) {
          // Server-backed type-ahead: refresh suggestions while typing. Shares
          // the row's seq guard with column switches so stale responses are
          // dropped; a failed refresh keeps the previous list (harmless).
          let timer: number | undefined
          input.addEventListener('input', () => {
            window.clearTimeout(timer)
            timer = window.setTimeout(() => {
              if (!input.isConnected) return // column switched while debouncing
              const mySeq = String(Number(row.dataset.seq ?? '0') + 1)
              row.dataset.seq = mySeq
              fetchSqlValues(tableSelect.value, column.name, input.value.trim())
                .then(({ values: next }) => {
                  if (row.dataset.seq === mySeq) fillDatalist(next)
                })
                .catch(() => undefined)
            }, TYPEAHEAD_DEBOUNCE_MS)
          })
        }
      }
    } catch (err) {
      if (row.dataset.seq !== seq || !row.isConnected) return
      handleFailure(err, row)
    }
  }

  function addFilterRow(): void {
    if (filtersEl.children.length >= MAX_FILTERS) return
    const row = document.createElement('div')
    row.className = 'sql-filter'
    const colSelect = document.createElement('select')
    colSelect.className = 'sql-filter__col'
    for (const column of columns) {
      const option = document.createElement('option')
      option.value = column.name
      option.textContent = column.label
      option.title = column.name // raw DB name — matches the map popup fields
      colSelect.appendChild(option)
    }
    // Two shapes for one slot: a plain label where the operator is imposed by
    // the column type, a picker where there is an actual choice to make.
    const eq = document.createElement('span')
    eq.className = 'sql-filter__op'
    const opSelect = document.createElement('select')
    opSelect.className = 'sql-filter__op-select'
    opSelect.setAttribute('aria-label', 'Comparison')
    for (const op of COMPARISON_OPS) {
      const option = document.createElement('option')
      option.value = op.value
      option.textContent = op.label
      option.title = op.title
      opSelect.appendChild(option)
    }
    const syncOpLabel = (): void => {
      const column = columns.find((c) => c.name === colSelect.value)
      const numeric = column !== undefined && isNumericColumn(column)
      opSelect.hidden = !numeric
      eq.hidden = numeric
      if (numeric) opSelect.value = '='
      else eq.textContent = column && isContainsColumn(column) ? 'contains' : '='
    }
    syncOpLabel()
    const valWrap = document.createElement('span')
    valWrap.className = 'sql-filter__valwrap'
    const remove = document.createElement('button')
    remove.type = 'button'
    remove.className = 'sql-filter__remove'
    remove.setAttribute('aria-label', 'Remove filter')
    remove.textContent = '×'
    remove.addEventListener('click', () => {
      row.remove()
      addFilterBtn.disabled = filtersEl.children.length >= MAX_FILTERS
    })
    colSelect.addEventListener('change', () => {
      syncOpLabel()
      const column = columns.find((c) => c.name === colSelect.value)
      if (column) void buildValueControl(row, column)
    })
    row.append(colSelect, eq, opSelect, valWrap, remove)
    filtersEl.appendChild(row)
    addFilterBtn.disabled = filtersEl.children.length >= MAX_FILTERS
    const first = columns.find((c) => c.name === colSelect.value)
    if (first) void buildValueControl(row, first)
  }

  function collectFilters(): SqlFilter[] {
    const filters: SqlFilter[] = []
    for (const row of filtersEl.querySelectorAll('.sql-filter')) {
      const column = (row.querySelector('.sql-filter__col') as HTMLSelectElement).value
      const valueEl = row.querySelector('.sql-filter__val') as HTMLSelectElement | HTMLInputElement | null
      const value = valueEl?.value ?? ''
      if (column && value !== '') {
        const spec = columns.find((c) => c.name === column)
        const picker = row.querySelector('.sql-filter__op-select') as HTMLSelectElement | null
        // The picker only exists — and is only shown — on number columns.
        const op: SqlFilter['op'] =
          picker && !picker.hidden
            ? (picker.value as SqlFilter['op'])
            : spec && isContainsColumn(spec)
              ? 'contains'
              : '='
        filters.push({ column, op, value })
      }
    }
    return filters
  }

  /**
   * Run the filter and hand the rows to the shared bottom table (T3). The form
   * stays on screen (T5): criteria and results are readable together, so
   * refining a search costs two clicks. On mobile the shell closes this panel
   * on its own when the table opens (`layoutShell` mobile exclusivity) — do not
   * duplicate that rule here.
   */
  async function run(): Promise<void> {
    errorEl.hidden = true
    errorOwner = null
    statusEl.innerHTML = `<p class="sql-results__muted">Running…</p>`
    const dataset = tableSelect.options[tableSelect.selectedIndex]?.textContent ?? tableSelect.value
    const filters = collectFilters()
    // Probe page 1 here rather than inside the table source: a plan/layer
    // refusal must be reported inline in this form, not as an empty table.
    try {
      const first = await runSqlQuery(tableSelect.value, filters, 1, PAGE_SIZE)
      statusEl.innerHTML = ''
      showDataTable({
        key: SQL_TABLE_KEY,
        title: `Filter · ${dataset}`,
        pageSize: PAGE_SIZE,
        columns: () => columns.map((c) => ({ key: c.name, label: c.label, title: c.name })),
        load: async (toPage) => {
          const result = toPage === 1 ? first : await pageOrReopen(filters, toPage)
          return {
            rows: result.rows.map(toRow),
            total: result.total,
            totalIsCapped: result.total_is_capped,
          }
        },
      })
    } catch (err) {
      statusEl.innerHTML = ''
      handleFailure(err)
    }
  }

  /** A failure while paging reports the error in the form, which never left. */
  async function pageOrReopen(filters: SqlFilter[], toPage: number) {
    try {
      return await runSqlQuery(tableSelect.value, filters, toPage, PAGE_SIZE)
    } catch (err) {
      handleFailure(err)
      throw err
    }
  }

  function toRow(row: Record<string, unknown>): DataTableRow {
    const bbox = row.bbox_3857 as [number, number, number, number] | null
    return {
      cells: row,
      onShow: bbox ? () => options.onShowBbox(bbox) : undefined,
    }
  }

  tableSelect.addEventListener('change', () => void loadColumns().catch(handleFailure))
  addFilterBtn.addEventListener('click', addFilterRow)
  el<HTMLButtonElement>('sql-run').addEventListener('click', () => void run())

  return {
    ensureLoaded() {
      if (loaded) return
      loaded = true
      void loadTables().catch((err) => {
        loaded = false
        handleFailure(err)
      })
    },
    dispose() {
      root.innerHTML = ''
    },
  }
}
